import { useEffect, useState, useRef } from "react";

const languages = [
  { code: "ru", name: "Русский", flag: "🇷🇺" },
  { code: "az", name: "Azərbaycan", flag: "🇦🇿" },
];

const GoogleTranslate = () => {
  const [currentLang, setCurrentLang] = useState(() => {
    const cookie = document.cookie.match(/googtrans=([^;]+)/);
    if (cookie) {
      const lang = cookie[1].split('/').pop();
      return lang || "ru";
    }
    return "ru";
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const dropdownRef = useRef(null);
  const scriptRef = useRef(null);

  // Закрываем дропдаун при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Функция для скрытия верхней панели
  const hideGoogleTranslateBar = () => {
    try {
      const style = document.createElement('style');
      style.innerHTML = `
        .goog-te-banner-frame,
        .skiptranslate,
        .goog-te-menu-frame {
          display: none !important;
        }
        body {
          top: 0 !important;
        }
      `;
      document.head.appendChild(style);
    } catch (error) {
      console.warn('Error hiding Google Translate bar:', error);
    }
  };

  // Функция инициализации Google Translate
  const initializeGoogleTranslate = () => {
    try {
      if (!window.google || !window.google.translate) {
        console.warn('Google Translate API not loaded yet');
        return;
      }

      if (document.getElementById('google_translate_element')) {
        new window.google.translate.TranslateElement(
          {
            pageLanguage: "ru",
            includedLanguages: "ru,az",
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          },
          "google_translate_element"
        );
        setIsInitialized(true);
        setTimeout(hideGoogleTranslateBar, 100);
      }
    } catch (error) {
      console.error('Error initializing Google Translate:', error);
    }
  };

  // Загрузка скрипта Google Translate
  useEffect(() => {
    const loadScript = () => {
      if (scriptRef.current || document.querySelector('script[src*="translate.google.com"]')) {
        return;
      }

      try {
        scriptRef.current = document.createElement("script");
        scriptRef.current.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
        scriptRef.current.async = true;
        scriptRef.current.onerror = (error) => {
          console.error('Error loading Google Translate script:', error);
          setIsScriptLoaded(false);
        };
        document.body.appendChild(scriptRef.current);
      } catch (error) {
        console.error('Error creating Google Translate script:', error);
      }
    };

    // Определяем функцию инициализации
    window.googleTranslateElementInit = () => {
      setIsScriptLoaded(true);
      initializeGoogleTranslate();
    };

    loadScript();

    // Периодически проверяем и скрываем верхнюю панель
    const interval = setInterval(hideGoogleTranslateBar, 100);

    return () => {
      clearInterval(interval);
      if (scriptRef.current && document.body.contains(scriptRef.current)) {
        document.body.removeChild(scriptRef.current);
      }
      delete window.googleTranslateElementInit;
    };
  }, []);

  // Повторная попытка инициализации, если скрипт загружен, но не инициализирован
  useEffect(() => {
    if (isScriptLoaded && !isInitialized) {
      const retryInterval = setInterval(() => {
        initializeGoogleTranslate();
        if (isInitialized) {
          clearInterval(retryInterval);
        }
      }, 1000);

      return () => clearInterval(retryInterval);
    }
  }, [isScriptLoaded, isInitialized]);

  const changeLanguage = (langCode) => {
    if (langCode === currentLang) {
      setIsOpen(false);
      return;
    }

    try {
      // Устанавливаем cookies для Google Translate
      const date = new Date();
      date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
      document.cookie = `googtrans=/ru/${langCode}; expires=${date.toUTCString()}; path=/`;
      document.cookie = `googtrans=/${langCode}/${langCode}; expires=${date.toUTCString()}; path=/`;

      // Перезагружаем страницу для применения изменений
      window.location.reload();
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0];

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: "8px 12px",
          background: "#ffffff",
          border: "1px solid #ddd",
          borderRadius: "6px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "14px",
          transition: "all 0.2s ease",
          minWidth: "120px",
          justifyContent: "space-between",
          opacity: isInitialized ? 1 : 0.7,
        }}
        disabled={!isInitialized}
        onMouseOver={(e) => {
          if (isInitialized) {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
          }
        }}
        onMouseOut={(e) => {
          if (isInitialized) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "16px" }}>{currentLanguage.flag}</span>
          <span>{currentLanguage.name}</span>
        </div>
        <span style={{ 
          transition: "transform 0.2s ease",
          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
        }}>
          ▼
        </span>
      </button>

      {isOpen && isInitialized && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          marginTop: "4px",
          background: "#ffffff",
          border: "1px solid #ddd",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          zIndex: 1000,
          overflow: "hidden",
        }}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              style={{
                width: "100%",
                padding: "8px 12px",
                background: currentLang === lang.code ? "#f0f0f0" : "#ffffff",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "14px",
                transition: "all 0.2s ease",
                textAlign: "left",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#f5f5f5";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = currentLang === lang.code ? "#f0f0f0" : "#ffffff";
              }}
            >
              <span style={{ fontSize: "16px" }}>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
      <div id="google_translate_element" style={{ display: "none" }} />
    </div>
  );
};

export default GoogleTranslate;
